import { z } from 'zod';
import { HexColorSchema, IsoTimestampSchema, UuidSchema } from './common';

export const MessageTypeSchema = z.object({
  id: UuidSchema,
  name: z.string().min(1),
  color: HexColorSchema,
  description: z.string().nullable(),
  icon: z.string().nullable(),
  createdAt: IsoTimestampSchema,
});

export const MessageTypeWithStatsSchema = MessageTypeSchema.extend({
  logs24h: z.number().int().nonnegative(),
});

export const CreateMessageTypeSchema = z.object({
  name: z
    .string()
    .min(1)
    .max(32)
    .regex(/^[A-Z][A-Z0-9_]*$/, 'must be uppercase A-Z, digits and underscores'),
  color: HexColorSchema,
  description: z.string().max(500).optional(),
  icon: z.string().max(64).optional(),
});

export const UpdateMessageTypeSchema = CreateMessageTypeSchema.partial();

export type MessageType = z.infer<typeof MessageTypeSchema>;
export type MessageTypeWithStats = z.infer<typeof MessageTypeWithStatsSchema>;
export type CreateMessageType = z.infer<typeof CreateMessageTypeSchema>;
export type UpdateMessageType = z.infer<typeof UpdateMessageTypeSchema>;

// Built-in message types and their canonical colors. Used by:
// - DB seed (`@owl/db/seed`) on a fresh install.
// - API bootstrap, which re-asserts them on startup (`onConflictDoNothing`).
// - Web UI, to offer a "Reset to default color" affordance per card.
// The frontend looks them up by `name`, so renames here are breaking.
export const DEFAULT_MESSAGE_TYPES = [
  {
    name: 'FATAL',
    color: '#DC2626',
    description: 'Critical failures that compromise the process. Require immediate intervention.',
  },
  {
    name: 'ERROR',
    color: '#EF4444',
    description: 'Recoverable errors affecting a single request or task.',
  },
  {
    name: 'WARN',
    color: '#F59E0B',
    description: 'Anomalies or edge conditions that do not yet degrade the service.',
  },
  {
    name: 'INFO',
    color: '#3B82F6',
    description: 'Expected operational events. Useful for auditing and metrics.',
  },
  {
    name: 'DEBUG',
    color: '#8B5CF6',
    description: 'Verbose traces for local debugging or non-production environments.',
  },
  {
    name: 'TRACE',
    color: '#6B7280',
    description: 'Maximum execution detail. Noisy, almost always filtered.',
  },
  {
    name: 'SUCCESS',
    color: '#22C55E',
    description:
      'Confirmed successful outcome of an external/internal call (200 OK or equivalent). Counts as success in App health.',
  },
  {
    name: 'HEALTH',
    color: '#10B981',
    description: 'Health checks, heartbeats and liveness probes. Excluded from App health metrics.',
  },
] as const satisfies ReadonlyArray<{ name: string; color: string; description: string }>;

export const DEFAULT_MESSAGE_TYPE_COLORS: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(DEFAULT_MESSAGE_TYPES.map((m) => [m.name, m.color])),
);
